import { Children, useId, type FunctionComponent } from "react";
import type { ISvgProps, IProps, IGroupProps } from "./sentiment-icon.types";
import classNames from "classnames";
import { Sentiment } from "@edgetier/watchtower-types";
import { Group } from "../group";
import styles from "./sentiment-icon.module.scss";
import { Center } from "../center";

const Frustration: FunctionComponent<ISvgProps> = ({ className, svgProps, isActive = true }) => {
    const id = useId();
    const gradientId = `paint0_linear_423_8390_${id}`;
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="18"
            viewBox="0 0 18 18"
            fill="none"
            className={classNames(styles["frustration"], className)}
            data-testid="frustration-icon"
            data-active={isActive}
            {...svgProps}
        >
            <circle cx="9" cy="9" r="9" fill={`url(#${gradientId})`} />
            <path
                d="M7.2316 8.56766C7.2316 8.95984 7.12031 9.31847 6.93625 9.59385C6.71283 9.92812 6.3822 10.1397 6.01326 10.1397C5.63863 10.1397 5.30348 9.92153 5.07999 9.57826C4.90208 9.305 4.79492 8.95248 4.79492 8.56766C4.79492 7.69944 5.34039 6.99561 6.01326 6.99561C6.68613 6.99561 7.2316 7.69944 7.2316 8.56766Z"
                fill="var(--path-fill)"
            />
            <path
                d="M12.8908 8.56766C12.8908 8.95984 12.7795 9.31847 12.5954 9.59385C12.372 9.92812 12.0414 10.1397 11.6724 10.1397C11.2978 10.1397 10.9627 9.92153 10.7392 9.57826C10.5613 9.305 10.4541 8.95248 10.4541 8.56766C10.4541 7.69944 10.9996 6.99561 11.6724 6.99561C12.3453 6.99561 12.8908 7.69944 12.8908 8.56766Z"
                fill="var(--path-fill)"
            />
            <path
                d="M7.47197 7.37217C7.3366 7.64044 6.43603 7.45886 5.46049 6.96661C4.48496 6.47435 3.80387 5.85781 3.93924 5.58954C4.07461 5.32127 5.83771 5.55026 6.81324 6.04252C7.78878 6.53478 7.60734 7.1039 7.47197 7.37217Z"
                fill="var(--path-fill)"
            />
            <path
                d="M10.2501 7.30375C10.3859 7.57312 11.2901 7.39092 12.2696 6.8968C13.2491 6.40269 13.933 5.78376 13.7971 5.5144C13.6613 5.24504 11.8911 5.47474 10.9116 5.96885C9.93208 6.46297 10.1142 7.03439 10.2501 7.30375Z"
                fill="var(--path-fill)"
            />
            <path
                d="M9.2666 12.2645C11.2934 12.1883 12.0225 13.8639 12.0225 13.8639C12.0572 14.0331 11.8344 14.1068 11.6871 14.0436C9.7991 13.2335 8.87932 13.3715 6.99134 14.0436C6.8146 14.118 6.51915 14.0331 6.62477 13.8006C6.62477 13.8006 7.5829 12.3278 9.2666 12.2645Z"
                fill="var(--path-fill)"
            />
            <defs>
                <linearGradient id={gradientId} x1="9" y1="0" x2="9" y2="18" gradientUnits="userSpaceOnUse">
                    <stop stopColor="var(--gradient-stop)" />
                    <stop offset="1" stopColor="var(--gradient-stop)" />
                </linearGradient>
            </defs>
        </svg>
    );
};
const Empathy: FunctionComponent<ISvgProps> = ({ className, svgProps, isActive = true }) => {
    const id = useId();
    const gradientId = `paint0_linear_423_7578_${id}`;
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="18"
            viewBox="0 0 18 18"
            fill="none"
            className={classNames(styles["empathy"], className)}
            data-testid="empathy-icon"
            data-active={isActive}
            {...svgProps}
        >
            <circle cx="9" cy="9" r="9" fill={`url(#${gradientId})`} />
            <path
                d="M11.5598 6.64872C12.1374 6.06282 12.1374 5.11176 11.5598 4.52585C10.9821 3.93995 10.0476 3.93995 9.46993 4.52585L9.12128 4.8807L8.77262 4.52585C8.19497 3.93995 7.26041 3.93995 6.68276 4.52585C6.10511 5.11176 6.10511 6.06282 6.68276 6.64872L8.67979 8.6767C8.80151 8.80048 8.96036 8.86237 9.12128 8.86237C9.2822 8.86237 9.44105 8.80048 9.56277 8.6767L11.5598 6.64872ZM3.61502 10.5664C3.35714 10.9172 3.43141 11.4082 3.78212 11.664L6.27015 13.498C6.73021 13.8364 7.28311 14.0179 7.85663 14.0179H11.0069H14.151C14.4996 14.0179 14.7802 13.7373 14.7802 13.3887V12.1302C14.7802 11.7816 14.4996 11.501 14.151 11.501H13.4289L12.5459 10.7934C12.1003 10.4385 11.5454 10.2467 10.9739 10.2467H9.43486H9.12128H7.86282C7.51417 10.2467 7.23359 10.5272 7.23359 10.8759C7.23359 11.2246 7.51417 11.5051 7.86282 11.5051H9.12128H9.43486C9.60816 11.5051 9.74844 11.6475 9.74844 11.8187C9.74844 11.9899 9.60609 12.1323 9.43486 12.1323H7.06649L4.71462 10.3993C4.3639 10.1415 3.8729 10.2157 3.61708 10.5664H3.61502ZM10.976 11.5031H10.9945C10.9883 11.5031 10.9821 11.5031 10.976 11.5031Z"
                fill="var(--path-fill)"
            />
            <defs>
                <linearGradient id={gradientId} x1="9" y1="0" x2="9" y2="18" gradientUnits="userSpaceOnUse">
                    <stop stopColor="var(--gradient-stop)" />
                    <stop offset="1" stopColor="var(--gradient-stop)" />
                </linearGradient>
            </defs>
        </svg>
    );
};
const Confusion: FunctionComponent<ISvgProps> = ({ className, svgProps, isActive = true }) => {
    const id = useId();
    const gradientId = `paint0_linear_423_7676_${id}`;
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="18"
            viewBox="0 0 18 18"
            fill="none"
            className={classNames(styles["confusion"], className)}
            data-testid="confusion-icon"
            data-active={isActive}
            {...svgProps}
        >
            <ellipse cx="9" cy="8.99989" rx="9" ry="8.99843" fill={`url(#${gradientId})`} />
            <path
                d="M7.12172 7.40268C7.12172 7.81083 7.00324 8.18407 6.80731 8.47067C6.56947 8.81855 6.21751 9.03876 5.82478 9.03876C5.42597 9.03876 5.06921 8.81169 4.8313 8.45444C4.6419 8.17005 4.52783 7.80317 4.52783 7.40268C4.52783 6.4991 5.10849 5.7666 5.82478 5.7666C6.54106 5.7666 7.12172 6.4991 7.12172 7.40268Z"
                fill="var(--path-fill)"
            />
            <path
                d="M12.1832 7.48056C12.1832 7.88871 12.0648 8.26195 11.8688 8.54855C11.631 8.89643 11.279 9.11664 10.8863 9.11664C10.4875 9.11664 10.1307 8.88957 9.89282 8.53232C9.70343 8.24794 9.58936 7.88105 9.58936 7.48056C9.58936 6.57698 10.17 5.84448 10.8863 5.84448C11.6026 5.84448 12.1832 6.57698 12.1832 7.48056Z"
                fill="var(--path-fill)"
            />
            <path
                d="M11.7531 13.3587C11.8998 13.3103 12.264 12.8134 11.8317 12.268C11.6745 12.1512 11.6467 12.099 11.3208 12.1122C7.46718 12.268 5.74639 13.1799 5.81858 13.3588C8.882 13.2042 10.7426 13.1637 11.3208 13.3198C11.4368 13.3511 11.6352 13.3977 11.7531 13.3587Z"
                fill="var(--path-fill)"
            />
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M16.383 4.1225C16.383 4.34057 16.2614 4.55511 16.0417 4.6835L15.3501 5.08949C14.8579 5.37869 14.5489 5.88539 14.5489 6.44113V6.46209C14.5489 6.9067 14.9342 7.22405 15.3559 7.22405C15.7776 7.22405 16.1629 6.9067 16.1629 6.46209V6.44113C16.1629 6.4273 16.1712 6.4019 16.2041 6.38232L16.8948 5.97689C17.5729 5.57721 17.9969 4.88227 17.9969 4.121V4.06711C17.9969 2.82916 16.9075 1.86816 15.6179 1.86816H15.0935C13.8043 1.86983 12.7148 2.83063 12.7148 4.06861C12.7148 4.51322 13.1002 4.83057 13.5218 4.83057C13.9435 4.83057 14.3288 4.51322 14.3288 4.06861C14.3288 3.71989 14.6476 3.39359 15.0939 3.39359H15.6179C16.0641 3.39359 16.383 3.71989 16.383 4.06861V4.1225Z"
                fill="var(--path-fill)"
            />
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M15.3559 9.97995C15.5969 9.97995 15.8325 9.89269 16.01 9.73048C16.1883 9.56749 16.2939 9.34071 16.2939 9.09824C16.2939 8.85577 16.1883 8.62899 16.01 8.46601C15.8325 8.3038 15.5969 8.21653 15.3559 8.21653C15.1149 8.21653 14.8793 8.3038 14.7018 8.46601C14.5235 8.62899 14.4179 8.85577 14.4179 9.09824C14.4179 9.34071 14.5235 9.56749 14.7018 9.73048C14.8793 9.89269 15.1149 9.97995 15.3559 9.97995Z"
                fill="var(--path-fill)"
            />
            <defs>
                <linearGradient
                    id={gradientId}
                    x1="9"
                    y1="0.00146484"
                    x2="9"
                    y2="17.9983"
                    gradientUnits="userSpaceOnUse"
                >
                    <stop stopColor="var(--gradient-stop)" />
                    <stop offset="1" stopColor="var(--gradient-stop)" />
                </linearGradient>
            </defs>
        </svg>
    );
};
const Praise: FunctionComponent<ISvgProps> = ({ className, svgProps, isActive = true }) => {
    const id = useId();
    const gradientId = `paint0_linear_762_9361_${id}`;
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="18"
            viewBox="0 0 18 18"
            fill="none"
            className={classNames(styles["praise"], className)}
            data-testid="praise-icon"
            data-active={isActive}
            {...svgProps}
        >
            <circle cx="9" cy="9" r="9" fill={`url(#${gradientId})`} />
            <path
                d="M10.2672 4.08841C10.842 4.20336 11.2156 4.76267 11.1006 5.33745L11.0498 5.58947C10.9326 6.17973 10.716 6.74125 10.4131 7.25192H13.5965C14.1823 7.25192 14.6576 7.72722 14.6576 8.31305C14.6576 8.72203 14.4255 9.07795 14.0851 9.25481C14.326 9.44935 14.4808 9.7478 14.4808 10.0816C14.4808 10.5989 14.1094 11.03 13.6208 11.1229C13.7181 11.2842 13.7734 11.4721 13.7734 11.6733C13.7734 12.1442 13.4661 12.5443 13.0416 12.6814C13.0571 12.7543 13.0659 12.8317 13.0659 12.9113C13.0659 13.4971 12.5906 13.9724 12.0048 13.9724H9.84937C9.42934 13.9724 9.02036 13.8486 8.67107 13.6165L7.81995 13.0484C7.2297 12.6549 6.87599 11.9917 6.87599 11.282V10.4353V9.37419V8.82372C6.87599 8.1782 7.17001 7.57026 7.67184 7.1657L7.83543 7.03527C8.42126 6.5666 8.8214 5.90781 8.96731 5.17386L9.01815 4.92184C9.13311 4.34706 9.69241 3.97345 10.2672 4.08841ZM4.04629 7.60563H5.46114C5.85243 7.60563 6.16856 7.92176 6.16856 8.31305V13.265C6.16856 13.6563 5.85243 13.9724 5.46114 13.9724H4.04629C3.655 13.9724 3.33887 13.6563 3.33887 13.265V8.31305C3.33887 7.92176 3.655 7.60563 4.04629 7.60563Z"
                fill="var(--path-fill)"
            />
            <defs>
                <linearGradient id={gradientId} x1="9" y1="0" x2="9" y2="18" gradientUnits="userSpaceOnUse">
                    <stop stopColor="var(--gradient-stop)" />
                    <stop offset="1" stopColor="var(--gradient-stop)" />
                </linearGradient>
            </defs>
        </svg>
    );
};
const Gratitude: FunctionComponent<ISvgProps> = ({ className, svgProps, isActive = true }) => {
    const id = useId();
    const gradientId = `paint0_linear_762_9334_${id}`;
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="18"
            viewBox="0 0 18 18"
            fill="none"
            className={classNames(styles["gratitude"], className)}
            data-testid="gratitude-icon"
            data-active={isActive}
            {...svgProps}
        >
            <circle cx="9" cy="9" r="9" fill={`url(#${gradientId})`} />
            <path
                d="M8.74276 12.2569C11.7987 12.3964 13.043 9.69533 13.043 9.69533C13.0953 9.3854 12.74 9.53475 12.5179 9.65047C9.58419 11.1793 8.78571 11.4892 5.50054 9.81123C5.23406 9.67511 4.74551 9.38542 4.90475 9.81123C4.90475 9.81123 6.20422 12.141 8.74276 12.2569Z"
                fill="var(--path-fill)"
            />
            <path
                d="M5.77224 4.87929C7.21248 4.74138 7.77799 7.05611 7.77799 7.05611C7.81317 7.34252 7.58849 7.1984 7.53167 7.17065C6.1983 6.05241 5.89873 5.72228 4.22394 7.05609C4.12338 7.10245 3.87205 7.22794 3.94243 6.94154C3.94243 6.94154 4.57582 4.99386 5.77224 4.87929Z"
                fill="var(--path-fill)"
            />
            <path
                d="M12.532 4.88247C13.9722 4.74455 14.5378 7.05928 14.5378 7.05928C14.5729 7.34569 14.3483 7.20158 14.2914 7.17383C12.9581 6.05558 12.6585 5.72545 10.9837 7.05926C10.8831 7.10562 10.6318 7.23111 10.7022 6.94471C10.7022 6.94471 11.3356 4.99704 12.532 4.88247Z"
                fill="var(--path-fill)"
            />
            <defs>
                <linearGradient id={gradientId} x1="9" y1="0" x2="9" y2="18" gradientUnits="userSpaceOnUse">
                    <stop stopColor="var(--gradient-stop)" />
                    <stop offset="1" stopColor="var(--gradient-stop)" />
                </linearGradient>
            </defs>
        </svg>
    );
};

/**
 * Sentiment icon component for displaying sentiment indicators (frustration, empathy, confusion, praise, gratitude).
 * @param {string} props.sentiment - The sentiment type to display.
 * @param {string} props.className - Additional CSS class names.
 * @param {object} props.svgProps - Additional props to pass to the SVG element.
 * @returns {JSX.Element | null} The SentimentIcon component or null if sentiment is invalid.
 */
const SentimentIcon = (props: IProps) => {
    switch (props.sentiment) {
        case Sentiment.Frustration:
            return <Frustration {...props} />;
        case Sentiment.Confusion:
            return <Confusion {...props} />;
        case Sentiment.Gratitude:
            return <Gratitude {...props} />;
        case Sentiment.Praise:
            return <Praise {...props} />;
        case Sentiment.Empathy:
            return <Empathy {...props} />;
        default:
            return null;
    }
};

/**
 * Group component for displaying multiple sentiment icons together with overlapping effect.
 * @param {string} props.className - Additional CSS class names.
 * @param {React.ReactNode} props.children - SentimentIcon components to group together.
 * @param {string} props.iconBorderColor - Background color for the icon borders.
 * @returns {JSX.Element} The SentimentIconGroup component.
 */
const SentimentIconGroup = ({ className, children, iconBorderColor, ...otherProps }: IGroupProps) => {
    return (
        <Group className={classNames(styles["group"], className)} {...otherProps}>
            {Children.map(children, (child, index) => (
                <Center
                    className={styles["iconWrapper"]}
                    bdrs="xl"
                    bg={iconBorderColor}
                    left={index * -5}
                    style={{ zIndex: index + 1 }}
                >
                    {child}
                </Center>
            ))}
        </Group>
    );
};

SentimentIcon.Frustration = Frustration;
SentimentIcon.Empathy = Empathy;
SentimentIcon.Confusion = Confusion;
SentimentIcon.Praise = Praise;
SentimentIcon.Gratitude = Gratitude;
SentimentIcon.Group = SentimentIconGroup;

export { SentimentIcon };
